"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Disclosure = exports.DisclosureButton = exports.DisclosureActions = exports.DisclosureHeading = exports.DisclosureGroup = exports.DisclosurePanel = void 0;
var styled_1 = __importDefault(require("@emotion/styled"));
var react_aria_components_1 = require("react-aria-components");
var theme_1 = require("../../theme");
var react_aria_components_2 = require("react-aria-components");
Object.defineProperty(exports, "DisclosurePanel", { enumerable: true, get: function () { return react_aria_components_2.DisclosurePanel; } });
/**
 * A grouping of related disclosures, sometimes called an accordion.
 * It supports both single and multiple expanded items.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/DisclosureGroup.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-disclosure-group--default}
 *
 * @example
 * ```tsx
 * <DisclosureGroup>
 *   <Disclosure>
 *     <DisclosureHeading>
 *       <DisclosureButton slot="trigger">
 *         Section Title
 *       </DisclosureButton>
 *       <DisclosureActions>
 *         <IconButton>
 *           <Icon name="edit" />
 *         </IconButton>
 *       </DisclosureActions>
 *     </DisclosureHeading>
 *     <DisclosurePanel>
 *       Section content
 *     </DisclosurePanel>
 *   </Disclosure>
 * </DisclosureGroup>
 * ```
 *
 * @param allowsMultipleExpanded - Whether multiple items can be expanded at the same time
 * @param isDisabled - Whether all items are disabled
 * @param expandedKeys - The currently expanded keys in the group (controlled)
 * @param defaultExpandedKeys - The initial expanded keys in the group (uncontrolled)
 * @param children - The children of the component. Can be ReactNode or a render function
 * @param className - The CSS className for the element. Can be string or a function
 * @param style - The CSS styles for the element. Can be CSSProperties or a function
 * @param onExpandedChange - Handler that is called when items are expanded or collapsed
 * @param id - The element's unique identifier
 */
exports.DisclosureGroup = (0, styled_1.default)(react_aria_components_1.DisclosureGroup)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n  flex-grow: 1;\n  cursor: default;\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n  flex-grow: 1;\n  cursor: default;\n"])), theme_1.theme.space.space8);
/**
 * A heading for a disclosure that contains the trigger button and optional actions.
 * Must be used within a Disclosure component.
 *
 * @example
 * ```tsx
 * <DisclosureHeading>
 *   <DisclosureButton slot="trigger">
 *     Section Title
 *   </DisclosureButton>
 *   <DisclosureActions>
 *     <IconButton>
 *       <Icon name="edit" />
 *     </IconButton>
 *   </DisclosureActions>
 * </DisclosureHeading>
 * ```
 *
 * @param children - The trigger button and optional actions to display
 */
exports.DisclosureHeading = (0, styled_1.default)(react_aria_components_1.Heading)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  justify-content: space-between;\n  margin: 0;\n  align-items: center;\n"], ["\n  display: flex;\n  justify-content: space-between;\n  margin: 0;\n  align-items: center;\n"])));
/**
 * A container for actions related to a disclosure.
 * Must be used as a sibling to DisclosureButton within a DisclosureHeading.
 *
 * @example
 * ```tsx
 * <DisclosureActions>
 *   <IconButton>
 *     <Icon name="edit" />
 *   </IconButton>
 * </DisclosureActions>
 * ```
 *
 * @param children - The action buttons to display
 */
exports.DisclosureActions = styled_1.default.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  display: flex;\n  align-items: center;\n  padding: ", " ", " ", " 0;\n"], ["\n  display: flex;\n  align-items: center;\n  padding: ", " ", " ", " 0;\n"])), theme_1.theme.space.space8, theme_1.theme.space.space16, theme_1.theme.space.space8);
/**
 * A button that opens and closes a disclosure.
 * Must be used within a DisclosureHeading.
 *
 * @example
 * ```tsx
 * <DisclosureButton slot="trigger">
 *   Section Title
 * </DisclosureButton>
 * ```
 *
 * @param slot - The slot to place the button in. Must be "trigger"
 * @param children - The content to display inside the button
 * @param isDisabled - Whether the button is disabled. Defaults to false
 * @param onPress - Handler that is called when the button is pressed
 */
exports.DisclosureButton = (0, styled_1.default)(react_aria_components_1.Button)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  display: flex;\n  gap: ", ";\n  color: ", ";\n  flex-grow: 1;\n  padding: ", " ", ";\n  align-items: center;\n  margin: 0;\n  background: none;\n  outline: none;\n  border: none;\n"], ["\n  display: flex;\n  gap: ", ";\n  color: ", ";\n  flex-grow: 1;\n  padding: ", " ", ";\n  align-items: center;\n  margin: 0;\n  background: none;\n  outline: none;\n  border: none;\n"])), theme_1.theme.space.space16, theme_1.theme.colors.contentPrimary, theme_1.theme.space.space12, theme_1.theme.space.space16);
/**
 * A collapsible section of content that can be expanded or collapsed.
 * It is composed of a header with a heading and trigger button, and a panel that contains the content.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/Disclosure.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-disclosure--default}
 *
 * @example
 * ```tsx
 * <Disclosure>
 *   <DisclosureHeading>
 *     <DisclosureButton slot="trigger">
 *       Section Title
 *     </DisclosureButton>
 *     <DisclosureActions>
 *       <IconButton>
 *         <Icon name="edit" />
 *       </IconButton>
 *     </DisclosureActions>
 *   </DisclosureHeading>
 *   <DisclosurePanel>
 *     Section content
 *   </DisclosurePanel>
 * </Disclosure>
 * ```
 *
 * @param id - An id for the disclosure when used within a DisclosureGroup, matching the id used in expandedKeys
 * @param isDisabled - Whether the disclosure is disabled
 * @param isExpanded - Whether the disclosure is expanded (controlled)
 * @param defaultExpanded - Whether the disclosure is expanded by default (uncontrolled)
 * @param backgroundType - The background type of the disclosure. Defaults to 'default'
 * @param children - The children of the component. Can be ReactNode or a render function
 * @param className - The CSS className for the element. Can be string or a function
 * @param style - The CSS styles for the element. Can be CSSProperties or a function
 * @param onExpandedChange - Handler that is called when the disclosure's expanded state changes
 */
exports.Disclosure = (0, styled_1.default)(react_aria_components_1.Disclosure)(function (_a) {
    var backgroundType = _a.backgroundType;
    return "\n  background: ".concat(backgroundType === 'failure'
        ? theme_1.theme.colors.surfaceNegative
        : theme_1.theme.colors.surface200, ";\n  padding: 0;\n  border: ").concat(theme_1.theme.borderWidths.regular, " solid ").concat(backgroundType === 'failure'
        ? theme_1.theme.colors.borderNegative
        : theme_1.theme.colors.borderNeutral, ";\n  border-radius: ").concat(theme_1.theme.radii.card, ";\n  outline: none;\n  display: flex;\n  flex-direction: column;\n\n  &:has(button[slot=\"trigger\"][data-focus-visible]) {\n    outline: ").concat(theme_1.theme.borderWidths.thick, " solid ").concat(theme_1.theme.colors.borderPrimary, ";\n    outline-offset: ").concat(theme_1.theme.space.space2, ";\n  }\n\n  cursor: default;\n  &:has(button[slot=\"trigger\"][data-hovered]) {\n    border-color: ").concat(backgroundType === 'failure'
        ? theme_1.theme.colors.borderNegativeHovered
        : theme_1.theme.colors.borderNeutralHovered, ";\n  }\n\n  &:has(button[slot=\"trigger\"][data-pressed]) {\n    border-color: ").concat(backgroundType === 'failure'
        ? theme_1.theme.colors.borderNegativePressed
        : theme_1.theme.colors.borderNeutralPressed, ";\n  }\n");
});
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
//# sourceMappingURL=index.js.map